<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import type { ProductionOrder } from '../types/planning'

/**
 * Schedule board on real production orders.
 *
 * Orders, dates and quantities come from `/production-orders`; the board groups
 * them by the real `department_name` because orders have no work-center column.
 */

const { t } = useI18n()
const { listProductionOrders } = usePlanningApi()

const PER_PAGE = 24
const today = new Date().toISOString().slice(0, 10)
const statuses = ['', 'planned', 'in_progress', 'completed', 'cancelled']

const orders = ref<ProductionOrder[]>([])
const loading = ref(true)
const error = ref('')
const page = ref(1)
const total = ref(0)
const totalPages = ref(1)
const filters = ref({ search: '', status: 'in_progress' })

const plannedQuantity = computed(() =>
  orders.value.reduce((sum, order) => sum + Number(order.planned_quantity ?? 0), 0),
)
const producedQuantity = computed(() =>
  orders.value.reduce((sum, order) => sum + Number(order.produced_quantity ?? 0), 0),
)
const inProgress = computed(() => orders.value.filter((order) => order.status === 'in_progress').length)

/** Real, verifiable warnings: past end date and missing dates. */
const overdue = computed(() =>
  orders.value.filter(
    (order) =>
      !!order.end_date &&
      order.end_date < today &&
      order.status !== 'completed' &&
      order.status !== 'cancelled',
  ),
)
const missingDates = computed(() => orders.value.filter((order) => !order.start_date || !order.end_date))

/** Columns are the departments the current page actually contains. */
const departments = computed(() => {
  const grouped = new Map<string, ProductionOrder[]>()
  for (const order of orders.value) {
    const key = order.department_name || '—'
    grouped.set(key, [...(grouped.get(key) ?? []), order])
  }
  return [...grouped.entries()]
    .map(([name, items]) => ({
      name,
      items,
      planned: items.reduce((sum, order) => sum + Number(order.planned_quantity ?? 0), 0),
    }))
    .sort((a, b) => b.planned - a.planned)
})

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  const to = Math.min(page.value * PER_PAGE, total.value)
  return t('planning.common.showing', { from, to, total: total.value })
})

function statusClass(status: string): string {
  if (status === 'completed') return 'status-ok'
  if (status === 'cancelled') return 'status-critical'
  if (status === 'in_progress') return 'status-info'
  return 'status-neutral'
}

function progressPercent(order: ProductionOrder): number {
  const planned = Number(order.planned_quantity ?? 0)
  if (planned <= 0) return 0
  return Math.min(100, Math.round((Number(order.produced_quantity ?? 0) / planned) * 100))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listProductionOrders({
      search: filters.value.search.trim() || undefined,
      status: filters.value.status || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    orders.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

watch(
  () => filters.value.status,
  () => {
    page.value = 1
    void load()
  },
)

watch(
  () => filters.value.search,
  (_value, _old, onCleanup) => {
    const timer = setTimeout(() => {
      page.value = 1
      void load()
    }, 300)
    onCleanup(() => clearTimeout(timer))
  },
)

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.eyebrow') }}</p>
      <h2>{{ t('planning.schedule.title') }}</h2>
      <p>{{ t('planning.schedule.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('planning.common.searchPlaceholder')"
    >
    <select v-model="filters.status" class="filter-select">
      <option v-for="status in statuses" :key="status || 'all'" :value="status">
        {{ status ? t(`planning.orderStatus.${status}`) : t('planning.common.allStatuses') }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
    <span class="spacer" />
    <RouterLink class="btn-primary" to="/rescheduling">
      {{ t('planning.schedule.openRescheduling') }}
    </RouterLink>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <div v-else-if="orders.length === 0" class="state-message">
    <p>{{ t('planning.schedule.noOrders') }}</p>
  </div>

  <template v-else>
    <div class="metric-grid">
      <section class="metric-card">
        <span class="metric-value">{{ total }}</span>
        <span class="metric-label">{{ t('planning.schedule.plannedOrders') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ plannedQuantity.toLocaleString() }}</span>
        <span class="metric-label">{{ t('planning.schedule.pairsScheduled') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ inProgress }}</span>
        <span class="metric-label">{{ t('planning.orderStatus.in_progress') }}</span>
      </section>
      <section class="metric-card">
        <span class="metric-value">{{ producedQuantity.toLocaleString() }}</span>
        <span class="metric-label">{{ t('planning.common.produced') }}</span>
      </section>
    </div>

    <section v-if="overdue.length || missingDates.length" class="notice-panel">
      <strong>{{ t('planning.schedule.warnings') }}</strong>
      <span>
        {{ t('planning.schedule.overdue') }}: {{ overdue.length }} ·
        {{ t('planning.schedule.missingDates') }}: {{ missingDates.length }}
      </span>
    </section>

    <section class="board">
      <article v-for="department in departments" :key="department.name" class="board-column">
        <header>
          <h3>{{ department.name }}</h3>
          <span>{{ department.planned.toLocaleString() }}</span>
        </header>
        <div class="order-stack">
          <RouterLink
            v-for="order in department.items"
            :key="order.id"
            class="order-card"
            :to="`/orders?focus=${order.mo_code}`"
          >
            <strong>{{ order.mo_code }}</strong>
            <span>{{ order.product_name || '—' }}</span>
            <small>
              {{ formatDisplayDate(order.start_date) || '—' }} –
              {{ formatDisplayDate(order.end_date) || '—' }}
            </small>
            <div class="badge-row">
              <span class="status-badge" :class="statusClass(order.status)">
                {{ t(`planning.orderStatus.${order.status}`) }}
              </span>
              <span class="badge badge--draft">
                {{ progressPercent(order) }}% ·
                {{ Number(order.produced_quantity ?? 0).toLocaleString() }} /
                {{ Number(order.planned_quantity ?? 0).toLocaleString() }}
              </span>
            </div>
          </RouterLink>
        </div>
      </article>
    </section>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button class="btn-secondary" :disabled="page <= 1 || loading" @click="goToPage(page - 1)">
        {{ t('planning.common.previous') }}
      </button>
      <span>{{ t('planning.common.page', { page, total: totalPages }) }}</span>
      <button class="btn-secondary" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">
        {{ t('planning.common.next') }}
      </button>
    </div>
  </template>
</template>
